/**
 * Normalize `/api/ois/report-view` payloads so Elite Pulse always sees camelCase `Report` fields.
 * OIS `REPORT_VIEW.json` / DB posts often use snake_case (`full_list`, `unusual_activity`) or wrap
 * the real object in `{ "report": { ... } }`.
 */

import type { Report } from '../types/report';

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function migrateArray(obj: Record<string, unknown>, camel: string, ...snakes: string[]): void {
  const cur = obj[camel];
  if (Array.isArray(cur) && cur.length > 0) return;
  for (const s of snakes) {
    const v = obj[s];
    if (Array.isArray(v) && v.length > 0) {
      obj[camel] = v;
      return;
    }
  }
  if (!Array.isArray(cur) || cur.length === 0) {
    for (const s of snakes) {
      const v = obj[s];
      if (Array.isArray(v)) {
        obj[camel] = v;
        return;
      }
    }
  }
}

function migrateObject(obj: Record<string, unknown>, camel: string, ...snakes: string[]): void {
  const cur = obj[camel];
  if (isRecord(cur)) return;
  for (const s of snakes) {
    const v = obj[s];
    if (isRecord(v)) {
      obj[camel] = v;
      return;
    }
  }
}

function normalizeMeta(m: unknown): Report['meta'] {
  if (!isRecord(m)) {
    return { title: 'Elite Pulse', generatedAt: new Date().toISOString() };
  }
  const mm = { ...m };
  if (!mm.generatedAt && mm.generated_at) mm.generatedAt = mm.generated_at;
  if (!mm.title && mm.report_title) mm.title = mm.report_title;
  if (!String(mm.generatedAt || '').trim()) mm.generatedAt = new Date().toISOString();
  if (!String(mm.title || '').trim()) mm.title = 'Elite Pulse';
  return mm as unknown as Report['meta'];
}

/**
 * Returns a new object (shallow clone + list migrations) typed as `Report`.
 */
export function normalizeElitePulseReportFromApi(raw: unknown): Report | null {
  if (!isRecord(raw)) return null;

  let o = raw;
  if (isRecord(o.report)) {
    const inner = o.report as Record<string, unknown>;
    const innerLooksLikeReport =
      inner.meta != null ||
      inner.fullList != null ||
      inner.full_list != null ||
      inner.tradingCalls != null ||
      inner.trading_calls != null ||
      inner.unusualActivity != null ||
      inner.unusual_activity != null ||
      inner.flowMatrix != null ||
      inner.flow_matrix != null ||
      inner.tactical != null ||
      inner.strategic != null;
    if (innerLooksLikeReport) o = inner;
  }

  const out = { ...o } as Record<string, unknown>;

  out.meta = normalizeMeta(out.meta ?? (out as { Meta?: unknown }).Meta);

  migrateObject(out, 'macro', 'Macro');
  if (!isRecord(out.macro)) out.macro = {};

  if (!isRecord(out.status)) {
    out.status = {
      context: String(out.status_context ?? out.context ?? '').trim() || '',
      focus: String(out.status_focus ?? out.focus ?? '').trim() || '',
    };
  }

  if (out.quickPlaysSummary == null || out.quickPlaysSummary === '') {
    const qp = out.quick_plays_summary ?? out.quickPlays;
    if (typeof qp === 'string') out.quickPlaysSummary = qp;
  }

  migrateArray(out, 'tactical', 'tactical_plays', 'tacticalPlays');
  migrateArray(out, 'strategic', 'strategic_plays', 'strategicPlays');
  migrateArray(out, 'tradingCalls', 'trading_calls', 'active_trading_calls');
  migrateArray(out, 'closedCalls', 'closed_calls');
  migrateArray(out, 'unusualActivity', 'unusual_activity');
  migrateArray(out, 'flowMatrix', 'flow_matrix');
  migrateArray(out, 'fullList', 'full_list');
  migrateArray(out, 'whaleArch', 'whale_arch');
  migrateArray(out, 'whaleShiftsPc', 'whale_shifts_pc');
  migrateArray(out, 'wallShifts', 'wall_shifts');
  migrateArray(out, 'alphaSniper', 'alpha_sniper');
  migrateArray(out, 'todayFreshFlows', 'today_fresh_flows');
  migrateArray(out, 'geneticSnipers', 'genetic_snipers');
  migrateArray(out, 'sovereignCore', 'sovereign_core');
  migrateArray(out, 'marketImpactPc', 'market_impact_pc');
  migrateArray(out, 'caseStudy', 'case_study');
  migrateArray(out, 'riskFlags', 'risk_flags');

  migrateObject(out, 'actionPlan', 'action_plan');

  if (out.performance == null && out.Performance != null) out.performance = out.Performance;

  if (out.strategicPulseV97 == null && out.strategic_pulse_v97 != null) {
    out.strategicPulseV97 = out.strategic_pulse_v97;
  }

  if (out.legend == null && out.Legend != null) out.legend = out.Legend;

  return out as unknown as Report;
}
